import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { FC, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Tooltip } from 'react-tooltip';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { getPermissions } from 'reduxStore/reducers/ProfileSlice/selectors/getPermissions';
import { FileDescriptorService } from 'services/FileDescriptorService';
import { PageWrapper } from 'ui/redesign/PageWrapper';
import { Table } from 'ui/redesign/Table';
import { TableCard } from 'ui/redesign/Table/TableCard';
import { TableLoading } from 'ui/redesign/Table/TableLoading';
import { getOrganizationId, handleFileDownloadNew } from 'utils/helpers';
import { AcceptForm } from './AcceptForm';
import { GenerateContractsTableHeadings } from './consts';
import { SubscribeForm } from './SubscribeForm';
import { EdmsService } from 'services/EdmsService';
import { NoneOrders } from 'pages/Orders/NoneOrders';
import { NoDocuments } from 'assets/icons';
import { ErrorResponse } from 'types';
import { AxiosError, AxiosResponse } from 'axios';
import { Contracts } from 'services/EdmsService/types';
import { toast } from 'react-toastify';

export const ContractsPage: FC = () => {
  const dispatch = useAppDispatch();
  const organizationId = getOrganizationId();
  const permissions = useAppSelector(getPermissions);
  const queryClient = useQueryClient();
  const { data, isFetching } = useQuery<AxiosResponse<Contracts>, AxiosError<ErrorResponse>>(
    ['getContracts'],
    () => EdmsService.getContracts(organizationId ?? '', 0, 999),
    {
      onError: (error) => {
        toast.error(error.response?.data.detail);
      },
    }
  );

  // Скачать файл
  const { mutate: contractFileDownload } = useMutation(
    [`contractFileDownload`],
    (readLink: string) => FileDescriptorService.downloadSingleFile(readLink || ''),
    {
      onSuccess: (res) => {
        handleFileDownloadNew(res.data);
      },
    }
  );

  // Скачать контракт
  const { mutate: getContractById } = useMutation(
    [`getContractById`],
    (id: string) => EdmsService.getContractById(id),
    {
      onSuccess: (res) => {
        res.data.file.readLink && contractFileDownload(res.data.file.readLink);
      },
    }
  );

  const subscribe = (
    id: string,
    number: string,
    name: string,
    date: string,
    concurrencyStamp: string
  ) => {
    dispatch(
      modalActions.handleOpenModal({
        content: (
          <SubscribeForm
            id={id}
            number={number}
            name={name}
            date={date}
            concurrencyStamp={concurrencyStamp}
          />
        ),
        options: {
          title: '',
          modalClassName: 'w-full',
        },
      })
    );
  };

  const accept = (id: string, concurrencyStamp: string, name: string) => {
    dispatch(
      modalActions.handleOpenModal({
        content: <AcceptForm id={id} concurrencyStamp={concurrencyStamp} name={name} />,
        options: {
          title: '',
          modalClassName: 'w-full',
        },
      })
    );
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries('getContracts');
    };
  }, []);

  return (
    <PageWrapper title="Документооборот ">
      {isFetching ? (
        <TableLoading />
      ) : (
        <>
          {!data?.data.totalCount ? (
            <NoneOrders
              icon={<NoDocuments className="h-16 w-16" />}
              heading="Данные отсутствуют"
              description={'Нет ни одного созданного договора'}
            />
          ) : (
            <TableCard>
              <Table
                tableHeading={GenerateContractsTableHeadings({
                  getContractById,
                  subscribe,
                  accept,
                  permissions,
                  organizationId,
                })}
                tableData={(data?.data.items || []) as unknown as TableData[]}
              />
            </TableCard>
          )}
        </>
      )}
      <Tooltip id="action-tooltip" place="left" style={{ zIndex: 9999 }} />
    </PageWrapper>
  );
};
